import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { postJson, readCredits, WEBHOOKS } from "../lib/webhooks";

export function AuthPanel({ open, onClose, onAuth, user, credits, onLogout, initialError }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [msg, setMsg] = useState(initialError || "");
  const [loading, setLoading] = useState(false);
  const [forgotPassword,setForgotPassword] = useState(false);
  const [recoveryEmail,setRecoveryEmail] = useState("");
  const [recoverySent,setRecoverySent] = useState(false);

  const submit = async () => {
    if (!email || !pass) return setMsg("Introduce email y contraseña.");
    setLoading(true); setMsg("");
    try {
      const url = mode === "login" ? WEBHOOKS.login : WEBHOOKS.register;
      const r = await postJson(url, { email, password: pass });

      if (r?.success === false) {
        setMsg(r.message || "No se pudo completar.");
        setLoading(false);
        return;
      }
      const { credits: c } = readCredits(r);
      if (c === null && !r?.simulated) {
        setMsg("Error: la respuesta del servidor no incluye créditos.");
        setLoading(false); return;
      }
      const token = r?.token || "";
      const refreshToken = r?.refresh_token || "";

      if (token || refreshToken) {
        localStorage.setItem(
          "nexeron_session",
          JSON.stringify({
            user: { email },
            token,
            refreshToken,
            credits: c !== null ? c : 0
          })
        );
      }
      
      onAuth?.(
        r.user || { email },
        token,
        refreshToken,
        c !== null ? c : 3
      );
    } catch (e) {
      setMsg("No se pudo completar. Inténtalo de nuevo.");
    } finally { setLoading(false); }
  };

  const handlePasswordRecovery = async () => {
    if (!recoveryEmail) {
      setMsg("Introduce tu email.");
      return;
    }

    try {
      setLoading(true);
      setMsg("");

      const r = await postJson(WEBHOOKS.recovery, {
        email: recoveryEmail
      });

      if (r?.success === false) {
        setMsg(r.message || "No se pudo solicitar la recuperación.");
        return;
      }

      setRecoverySent(true);

    } catch (e) {
      setMsg("Error al solicitar recuperación.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 z-[80] flex items-center justify-center px-4"
          data-testid="auth-modal"
        >
          <div className="absolute inset-0 bg-black/75 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-md rounded-2xl bg-[rgb(var(--ink-2))]/95 border border-white/10 backdrop-blur-xl p-8 md:p-9 shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
          >
            {/* HUD corners */}
            <span className="absolute top-3 left-3 h-3 w-3 border-l border-t border-[rgb(var(--brand))]/60" />
            <span className="absolute top-3 right-3 h-3 w-3 border-r border-t border-[rgb(var(--brand))]/60" />
            <span className="absolute bottom-3 left-3 h-3 w-3 border-l border-b border-[rgb(var(--brand))]/60" />
            <span className="absolute bottom-3 right-3 h-3 w-3 border-r border-b border-[rgb(var(--brand))]/60" />

            <div className="flex items-center justify-between mb-6">
              <div>
                <span className="tag" style={{ borderColor: "rgba(92,225,230,0.3)", color: "rgb(var(--brand))" }}>Acceso</span>
                <h3 className="font-display font-light text-3xl mt-2 text-[rgb(var(--paper))]">
                  {user ? "Tu cuenta" : mode === "login" ? "Entrar" : (<><span className="italic font-serif">Crear</span> cuenta</>)}
                </h3>
              </div>
              <button onClick={onClose} className="text-[rgb(var(--paper-dim))] hover:text-[rgb(var(--paper))]" data-testid="close-auth">✕</button>
            </div>

            {user ? (
              <div className="space-y-4">
                <div className="rounded-xl border border-white/10 p-4 flex items-center justify-between bg-white/[0.02]">
                  <div>
                    <div className="text-xs text-[rgb(var(--paper-dim))] font-mono uppercase tracking-widest">Email</div>
                    <div className="text-[rgb(var(--paper))]">{user.email}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-[rgb(var(--paper-dim))] font-mono uppercase tracking-widest">Créditos</div>
                    {credits === null ? (
                      <div className="text-red-400 font-display italic text-2xl" data-testid="account-credits-error">Error</div>
                    ) : (
                      <div className="text-[rgb(var(--brand))] font-display italic text-2xl" data-testid="account-credits">{credits}</div>
                    )}
                  </div>
                </div>
                <button
                  onClick={onLogout}
                  className="w-full rounded-full py-3 border border-white/10 text-[rgb(var(--paper))] hover:bg-white/5"
                  data-testid="logout-btn"
                >
                  Cerrar sesión
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3 py-1">
                  <div className="h-px flex-1 bg-white/10" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[rgb(var(--paper-dim))]">email</span>
                  <div className="h-px flex-1 bg-white/10" />
                </div>
                <input
                  type="email" placeholder="Email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-[rgb(var(--paper))] outline-none focus:border-[rgb(var(--brand))]"
                  data-testid="auth-email"
                />
                <input
                  type="password" placeholder="Contraseña"
                  value={pass} onChange={(e) => setPass(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-[rgb(var(--paper))] outline-none focus:border-[rgb(var(--brand))]"
                  data-testid="auth-password"
                />
                <button
                  onClick={submit}
                  disabled={loading}
                  className="w-full rounded-full py-3 bg-[rgb(var(--brand))] text-[rgb(var(--ink))] text-sm font-medium disabled:opacity-60 hover:bg-[rgb(var(--paper))] transition-colors"
                  data-testid="auth-submit"
                >
                  {loading ? "Conectando…" : mode === "login" ? "Entrar" : "Crear cuenta"}
                </button>
                <button
                  onClick={() => setMode(mode === "login" ? "register" : "login")}
                  className="w-full text-xs text-[rgb(var(--paper-dim))] hover:text-[rgb(var(--paper))] mt-2"
                  data-testid="toggle-auth-mode"
                >
                  {mode === "login" ? "¿No tienes cuenta? Regístrate" : "Ya tengo cuenta"}
                </button>
                {msg && <p className="text-[11px] text-[rgb(var(--paper-dim))] font-mono">{msg}</p>}
                <button
                  type="button"
                  onClick={() => setForgotPassword(true)}
                  className="mt-4 text-sm text-gray-400 hover:text-white"
                >
                  ¿Has olvidado tu contraseña?
                </button>
                {forgotPassword && (
                  <div className="mt-6">

                    <h3 className="text-xl mb-3">
                      Recuperar contraseña
                    </h3>


                    {recoverySent ? (

                      <p>
                        Hemos recibido tu solicitud. Te contactaremos pronto.
                      </p>

                    ) : (

                      <>
                        <input
                          type="email"
                          placeholder="Tu email"
                          value={recoveryEmail}
                          onChange={(e)=>setRecoveryEmail(e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-[rgb(var(--paper))] outline-none focus:border-[rgb(var(--brand))]"
                        />


                        <button
                          onClick={handlePasswordRecovery}
                          disabled={loading}
                          className="w-full rounded-full py-3 bg-[rgb(var(--brand))] text-[rgb(var(--ink))] text-sm font-medium disabled:opacity-60 hover:bg-[rgb(var(--paper))] transition-colors mt-4"
                        >
                          {loading ? "Enviando…" : "Solicitar recuperación"}
                        </button>
                      </>

                    )}


                    <button
                      onClick={()=>setForgotPassword(false)}
                      className="mt-3 text-sm"
                    >
                      Volver
                    </button>

                  </div>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
